import { useAlert } from '@ads/components-react';

import { useNavigate } from '@tanstack/react-router';

import { useAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { usePostApiV1NotificationMobileApp } from '@/shared/api/generated/hooks/notificationMobileAppController/usePostApiV1NotificationMobileApp';

import { usePutApiV1NotificationMobileAppNotificationid } from '@/shared/api/generated/hooks/notificationMobileAppController/usePutApiV1NotificationMobileAppNotificationid';

import { notificationWizardAtom } from '../store/atoms/notificationWizardAtom';
import {
  ENotificationMobileStatus,
  type NotificationAudienceState,
  type NotificationMessageState,
  type NotificationScheduleState,
  type NotificationWizardState,
} from '../types/notification';

const WIZARD_TOTAL_STEPS = 3;

/**
 * Hook para gerenciar o estado do wizard de envio de notificação.
 * Encapsula o `notificationWizardAtom` e expõe navegação e atualizações de dados.
 *
 * @returns Objeto com step atual, estado completo, handlers de navegação e atualização
 *
 * @example
 * ```tsx
 * const { step, goNext, goPrev, goToStep, updateAudience, reset } = useNotificationWizard();
 * ```
 */
export function useNotificationWizard() {
  const [state, setState] = useAtom(notificationWizardAtom);
  const reset = useResetAtom(notificationWizardAtom);
  const { alert } = useAlert();
  const { t } = useTranslation('notification');
  const navigate = useNavigate();

  const notificationMutation = usePostApiV1NotificationMobileApp();
  const updateMutation = usePutApiV1NotificationMobileAppNotificationid();
  const isDraftOperationRef = useRef(false);

  const goNext = () =>
    setState((s: NotificationWizardState) => ({
      ...s,
      step: Math.min(s.step + 1, WIZARD_TOTAL_STEPS - 1),
    }));

  const goPrev = () =>
    setState((s: NotificationWizardState) => ({
      ...s,
      step: Math.max(s.step - 1, 0),
    }));

  const goToStep = (step: number) => setState((s: NotificationWizardState) => ({ ...s, step }));

  const updateAudience = (partial: Partial<NotificationAudienceState>) =>
    setState((s: NotificationWizardState) => ({
      ...s,
      audience: { ...s.audience, ...partial },
    }));

  const updateMessage = (partial: Partial<NotificationMessageState>) =>
    setState((s: NotificationWizardState) => ({
      ...s,
      message: { ...s.message, ...partial },
    }));

  const updateSchedule = (partial: Partial<NotificationScheduleState>) =>
    setState((s: NotificationWizardState) => ({
      ...s,
      schedule: { ...s.schedule, ...partial },
    }));

  const sendNotification = () => {
    isDraftOperationRef.current = false;
    const isScheduled = state.schedule.mode === 'scheduled';
    const payload = {
      title: state.message.title || null,
      message: state.message.body || null,
      classStudentIds: state.audience.classroomIds.map(Number),
      allowedEnrollmentStatus: state.audience.enrollmentStatuses.map(Number),
      urlExternal: state.message.url || null,
      scheduledAtUtc: isScheduled ? state.schedule.scheduledAt : null,
      notificationType: isScheduled
        ? ENotificationMobileStatus.Scheduled
        : ENotificationMobileStatus.Processing,
    };

    const callbacks = {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: isScheduled
            ? t('wizard.toast.scheduled.successTitle')
            : t('wizard.toast.send.successTitle'),
          description: isScheduled
            ? t('wizard.toast.scheduled.successDescription')
            : t('wizard.toast.send.successDescription'),
        });
        reset();
        navigate({ to: '/notification' });
      },
      onError: () =>
        alert({
          alertType: 'warning',
          title: isScheduled
            ? t('wizard.toast.scheduled.errorTitle')
            : t('wizard.toast.send.errorTitle'),
          description: isScheduled
            ? t('wizard.toast.scheduled.errorDescription')
            : t('wizard.toast.send.errorDescription'),
        }),
    };

    if (state.notificationId !== undefined) {
      updateMutation.mutate({ notificationId: state.notificationId, data: payload }, callbacks);
    } else {
      notificationMutation.mutate({ data: payload }, callbacks);
    }
  };

  const saveDraft = async (): Promise<boolean> => {
    isDraftOperationRef.current = true;
    const draftPayload = {
      title: state.message.title,
      message: state.message.body,
      classStudentIds: state.audience.classroomIds.map(Number),
      allowedEnrollmentStatus: state.audience.enrollmentStatuses.map(Number),
      urlExternal: state.message.url || null,
      scheduledAtUtc: null,
      notificationType: ENotificationMobileStatus.Draft,
    };

    const draftCallbacks = {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: t('wizard.toast.draft.successTitle'),
          description: t('wizard.toast.draft.successDescription'),
        });
        navigate({ to: '/notification' });
      },
      onError: () =>
        alert({
          alertType: 'warning',
          title: t('wizard.toast.draft.errorTitle'),
          description: t('wizard.toast.draft.errorDescription'),
        }),
    };

    try {
      if (state.notificationId !== undefined) {
        await updateMutation.mutateAsync(
          { notificationId: state.notificationId, data: draftPayload },
          draftCallbacks,
        );
      } else {
        await notificationMutation.mutateAsync({ data: draftPayload }, draftCallbacks);
      }
      return true;
    } catch {
      return false;
    }
  };

  return {
    step: state.step,
    wizardState: state,
    goNext,
    goPrev,
    goToStep,
    updateAudience,
    updateMessage,
    updateSchedule,
    sendNotification,
    isSending:
      (notificationMutation.isPending || updateMutation.isPending) && !isDraftOperationRef.current,
    saveDraft,
    isSavingDraft:
      (notificationMutation.isPending || updateMutation.isPending) && isDraftOperationRef.current,
    reset,
  } as const;
}
